/**
 * The share string (SHR-001, PRD §4 "Share text", K-09).
 *
 *   Potted · 9ft · 7 balls
 *   8 strokes (Par 7) · Chalk Up
 *   🟢🟢⚪🟢🔴🟢🟢
 *
 * Spoiler-free by construction: it is built only from the table size, the ball count,
 * the score, par, and one mark per shot. Nothing here can carry a ball position, an
 * aim or a layout, because none of those are inputs.
 *
 * It is DISPLAY, not a claim. A score in a share string is what the player's own screen
 * says, exactly like a screenshot; nothing trusts it (PRD N3). Scores that count are
 * computed by the server from the shot log (Phase 4).
 *
 * The puzzle number (SHR-002) comes from the table's own date, which the SERVER chose, so
 * a device clock cannot change it. None while `LAUNCH.isPlaceholder` is true (D-003).
 */
import { COUNTS, LABELS, LAUNCH, SCORE_LABELS, SHARE, SITE } from '../config/site.ts';

/** Days from one `YYYY-MM-DD` to another, by the calendar: no zone, so no DST hour. */
function daysBetween(from: string, to: string): number {
  const utc = (key: string): number =>
    Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)));
  return Math.round((utc(to) - utc(from)) / 86_400_000);
}

/**
 * A day's puzzle number: `LAUNCH.date` is #1 (D-034). `undefined` before it, or while
 * the launch date is still a placeholder, so a number is never shown that could change.
 */
export function puzzleNumber(
  dateKey: string,
  launch: { readonly date: string; readonly isPlaceholder: boolean } = LAUNCH,
): number | undefined {
  if (launch.isPlaceholder) return undefined;
  const days = daysBetween(launch.date, dateKey);
  return Number.isInteger(days) && days >= 0 ? days + 1 : undefined;
}

/** The PRD §4 label for a score against par. `SCORE_LABELS` ends in a catch-all. */
export function resultLabel(score: number, par: number): string {
  const delta = score - par;
  const band = SCORE_LABELS.find((b) => delta <= b.atMost);
  // The final band is a catch-all (atMost: Infinity), so a band is always found.
  return band?.label ?? SCORE_LABELS.at(-1)?.label ?? '';
}

export type ShotMark = keyof typeof SHARE.shotEmoji;

/**
 * One shot's mark. A scratch outranks a pot: it is the thing that cost a stroke. Two
 * or more balls in one shot is its own mark, since that is how par is beaten.
 */
export function shotMark(outcome: {
  readonly pottedIds: readonly number[];
  readonly scratched: boolean;
}): ShotMark {
  if (outcome.scratched) return 'scratch';
  if (outcome.pottedIds.length >= 2) return 'multi';
  return outcome.pottedIds.length === 1 ? 'potted' : 'miss';
}

export interface ResultSummary {
  readonly sizeFt: number;
  readonly ballCount: number;
  /** One per shot taken, in order. */
  readonly marks: readonly ShotMark[];
  /** Shots plus penalty strokes (PRD §4). */
  readonly score: number;
  /** Only for a solved level. */
  readonly par?: number | undefined;
  /** Only once the launch date is real (see above). */
  readonly puzzleNumber?: number | undefined;
}

export function shareText(result: ResultSummary): string {
  const sep = SHARE.separator;
  const name =
    result.puzzleNumber === undefined ? SITE.name : `${SITE.name} #${String(result.puzzleNumber)}`;
  const balls = `${String(result.ballCount)} ${plural(result.ballCount, COUNTS.ball)}`;
  const strokes = `${String(result.score)} ${plural(result.score, COUNTS.stroke)}`;
  const scoreLine =
    result.par === undefined
      ? strokes
      : `${strokes} (${LABELS.par} ${String(result.par)})${sep}${resultLabel(result.score, result.par)}`;
  const emoji = result.marks.map((mark) => SHARE.shotEmoji[mark]).join('');
  return [`${name}${sep}${String(result.sizeFt)}ft${sep}${balls}`, scoreLine, emoji].join('\n');
}

/**
 * What "Copy result" copies (SHR-004, owner 2026-09-30): the result, then the link on
 * its own line, as the X post has it. It replaced a separate "Copy link" button: a
 * pasted result is the share, and it should bring people back.
 */
export function copiedResult(text: string, link: string): string {
  return `${text}
${link}`;
}

function plural(value: number, forms: readonly [string, string]): string {
  return value === 1 ? forms[0] : forms[1];
}

/**
 * SHR-003 (owner, 2026-09-27): sharing straight to X and Facebook. Plain links to each
 * site's own share page, opened in a new tab: no script of theirs on our page, so the
 * strict CSP is untouched, and nothing reaches them unless the player taps.
 */
export const X_SHARE_URL = 'https://x.com/intent/post';
export const FACEBOOK_SHARE_URL = 'https://www.facebook.com/sharer/sharer.php';

/** A post on X: the result, then the link on its own line. */
export function xShareUrl(text: string, link: string): string {
  const url = new URL(X_SHARE_URL);
  url.searchParams.set('text', `${text}\n${link}`);
  return url.toString();
}

/**
 * Facebook shares a link and nothing else (it ignores any text), so this is the link;
 * the preview comes from the page's Open Graph card (SEO-001).
 */
export function facebookShareUrl(link: string): string {
  const url = new URL(FACEBOOK_SHARE_URL);
  url.searchParams.set('u', link);
  return url.toString();
}
